import React, { useState, useEffect } from 'react';
import { X, Upload, Trash2, Download, DollarSign, FileText, Building, User, MapPin } from 'lucide-react';
import { invoiceAPI } from "../../services/api";
import { toast } from "react-toastify";

const EditInvoiceModal = ({ invoice, candidates, clients, onClose, onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    clientId: '',
    clientName: '',
    clientEmail: '',
    poNumber: '',
    invoiceNumber: '',
    clientGSTIN: '',
    gstStatus: 'Registered',
    paymentTerms: '',
    billingAddress: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      country: 'India',
      zipcode: ''
    },
    sameAsBilling: true,
    shippingAddress: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      country: 'India',
      zipcode: ''
    },
    candidateId: '',
    candidateName: '',
    designation: '',
    placementType: 'Permanent',
    ctc: '',
    joiningDate: '',
    feeModel: 'Percentage',
    feePercentage: '',
    fixedAmount: '',
    calculatedFee: '',
    guaranteeDays: 90,
    guaranteeEndDate: '',
    ifCandidateLeaves: 'Full Refund',
    exitPenalty: '',
    yourGSTIN: '',
    yourPan: '',
    accountNumber: '',
    ifscCode: '',
    fromAddress: {
      streetLine1: '',
      streetLine2: '',
      city: '',
      state: '',
      country: 'India',
      zipcode: ''
    },
    isTDSApplicable: false,
    automaticPaymentReminders: false,
    sendInvoiceMailTo: ''
  });

  // Initialize form with invoice data
  useEffect(() => {
    if (invoice) {
      setFormData({
        clientId: invoice.clientId || '',
        clientName: invoice.clientName || '',
        clientEmail: invoice.clientEmail || '',
        poNumber: invoice.poNumber || invoice.invoiceNumber || '',
        invoiceNumber: invoice.invoiceNumber || '',
        clientGSTIN: invoice.clientGSTIN || '',
        gstStatus: invoice.gstStatus || 'Registered',
        paymentTerms: invoice.paymentTerms || '',
        billingAddress: invoice.billingAddress || {
          streetLine1: '',
          streetLine2: '',
          city: '',
          state: '',
          country: 'India',
          zipcode: ''
        },
        sameAsBilling: invoice.sameAsBilling !== undefined ? invoice.sameAsBilling : true,
        shippingAddress: invoice.shippingAddress || {
          streetLine1: '',
          streetLine2: '',
          city: '',
          state: '',
          country: 'India',
          zipcode: ''
        },
        candidateId: invoice.candidateId || '',
        candidateName: invoice.candidateName || '',
        designation: invoice.designation || '',
        placementType: invoice.placementType || 'Permanent',
        ctc: invoice.ctc || '',
        joiningDate: invoice.joiningDate || '',
        feeModel: invoice.feeModel || 'Percentage',
        feePercentage: invoice.feePercentage || '',
        fixedAmount: invoice.fixedAmount || '',
        calculatedFee: invoice.calculatedFee || '',
        guaranteeDays: invoice.guaranteeDays || 90,
        guaranteeEndDate: invoice.guaranteeEndDate || '',
        ifCandidateLeaves: invoice.ifCandidateLeaves || 'Full Refund',
        exitPenalty: invoice.exitPenalty || '',
        yourGSTIN: invoice.yourGSTIN || '',
        yourPan: invoice.yourPan || '',
        accountNumber: invoice.accountNumber || '',
        ifscCode: invoice.ifscCode || '',
        fromAddress: invoice.fromAddress || {
          streetLine1: '',
          streetLine2: '',
          city: '',
          state: '',
          country: 'India',
          zipcode: ''
        },
        isTDSApplicable: invoice.isTDSApplicable || false,
        automaticPaymentReminders: invoice.automaticPaymentReminders || false,
        sendInvoiceMailTo: invoice.sendInvoiceMailTo || ''
      });

      // Set existing documents
      if (invoice.documents && Array.isArray(invoice.documents)) {
        setExistingFiles(invoice.documents);
      }
    }
  }, [invoice]);

  // Calculate fee based on model
  useEffect(() => {
    let calculatedFee = 0;
    if (formData.feeModel === 'Percentage' && formData.ctc && formData.feePercentage) {
      calculatedFee = (parseFloat(formData.ctc) * parseFloat(formData.feePercentage)) / 100;
    } else if (formData.feeModel === 'Fixed' && formData.fixedAmount) {
      calculatedFee = parseFloat(formData.fixedAmount);
    } else if (formData.feeModel === 'Hybrid' && formData.ctc && formData.feePercentage && formData.fixedAmount) {
      calculatedFee = (parseFloat(formData.ctc) * parseFloat(formData.feePercentage)) / 100 + parseFloat(formData.fixedAmount);
    }
    setFormData(prev => ({ ...prev, calculatedFee: calculatedFee.toFixed(2) }));
  }, [formData.feeModel, formData.ctc, formData.feePercentage, formData.fixedAmount]);

  // Calculate guarantee end date
  useEffect(() => {
    if (formData.joiningDate && formData.guaranteeDays) {
      const joiningDate = new Date(formData.joiningDate);
      const guaranteeEndDate = new Date(joiningDate);
      guaranteeEndDate.setDate(guaranteeEndDate.getDate() + parseInt(formData.guaranteeDays));
      setFormData(prev => ({
        ...prev,
        guaranteeEndDate: guaranteeEndDate.toISOString().split('T')[0]
      }));
    }
  }, [formData.joiningDate, formData.guaranteeDays]);

  // Copy billing address to shipping if sameAsBilling is true
  useEffect(() => {
    if (formData.sameAsBilling) {
      setFormData(prev => ({
        ...prev,
        shippingAddress: { ...prev.billingAddress }
      }));
    }
  }, [formData.sameAsBilling, formData.billingAddress]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [field]: value
      }
    }));
  };

 const handleClientChange = (e) => {
  const selectedClient = clients.find(c => c._id === e.target.value);

  if (!selectedClient) return;

  setFormData(prev => ({
    ...prev,
    clientId: selectedClient._id,
    clientName: selectedClient.clientName || '',
    clientEmail: selectedClient.email || '',
    clientGSTIN: selectedClient.gstin || '',
    billingAddress: selectedClient.billingAddress || prev.billingAddress
  }));
};

  const handleCandidateChange = (e) => {
    const selectedCandidate = candidates.find(c => c.id === e.target.value);
    if (selectedCandidate) {
      setFormData(prev => ({
        ...prev,
        candidateId: selectedCandidate.id,
        candidateName: `${selectedCandidate.firstName} ${selectedCandidate.lastName}`
      }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeNewFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (fileUrl) => {
    setFilesToDelete(prev => [...prev, fileUrl]);
    setExistingFiles(prev => prev.filter(file => file.link !== fileUrl));
  };

  const handleDownloadFile = async (fileUrl) => {
    try {
      const response = await invoiceAPI.getDocumentDownloadUrl(fileUrl);
      const downloadUrl = response.data.downloadUrl;
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      
      // Add JSON data
      const enrichedFormData = {
        ...formData,
        invoiceDate: invoice.invoiceDate || new Date().toISOString().split('T')[0],
        tdsRate: invoice.tdsRate ?? 0,
        tdsAmount: invoice.tdsAmount ?? 0,
        netPayable: (parseFloat(formData.calculatedFee) || 0) - (invoice.tdsAmount ?? 0),
      };
      formDataToSend.append('placementOrderData', JSON.stringify(enrichedFormData));
      
      // Add new files
      files.forEach((file) => {
        formDataToSend.append('files', file);
      });
      
      // Add document types for new files
      const documentTypes = files.map(() => 'invoice').join(',');
      if (documentTypes) {
        formDataToSend.append('documentTypes', documentTypes);
      }
      
      // Add files to delete
      if (filesToDelete.length > 0) {
        formDataToSend.append('deleteFileUrls', filesToDelete.join(','));
      }

      await invoiceAPI.updateInvoice(invoice.id, formDataToSend);
      toast.success('Invoice updated successfully');
      onSave();
    } catch (error) {
      console.error('Error updating invoice:', error);
      toast.error(error.response?.data?.message || 'Failed to update invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const yourGSTIN = formData.yourGSTIN?.trim();
      const clientGSTIN = formData.clientGSTIN?.trim();
      if (yourGSTIN && yourGSTIN.length !== 15) {
        toast.error('Your GSTIN must be exactly 15 characters');
        return;
      }
      if (clientGSTIN && clientGSTIN.length !== 15) {
        toast.error('Client GSTIN must be exactly 15 characters');
        return;
      }
      if (yourGSTIN && clientGSTIN && yourGSTIN === clientGSTIN) {
        toast.error('Your GSTIN and Client GSTIN cannot be the same');
        return;
      }
    }
    setCurrentStep(prev => prev + 1);
  };

  const steps = [
    { number: 1, title: 'Client Details', icon: <Building size={18} /> },
    { number: 2, title: 'Placement Details', icon: <User size={18} /> },
    { number: 3, title: 'Fee Structure', icon: <DollarSign size={18} /> },
    { number: 4, title: 'Company & Documents', icon: <Upload size={18} /> }
  ];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50 backdrop-blur-sm">
      {/* Overlay - click to close */}
      <div className="absolute inset-0" onClick={onClose}></div>
      
      {/* Right Side Panel */}
      <div className="relative bg-white w-full max-w-3xl h-full flex flex-col shadow-2xl animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div>
            <h2 className="text-2xl font-bold">Edit Invoice</h2>
            <p className="text-sm text-blue-100 mt-1">Update placement order details</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-blue-500 rounded-full"
          >
            <X size={24} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors cursor-pointer ${
                      currentStep >= step.number
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                    onClick={() => setCurrentStep(step.number)}
                  >
                    {step.icon}
                  </div>
                  <span className="text-xs mt-2 text-center font-medium">{step.title}</span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-2 ${
                      currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                    }`}
                  ></div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Client Details */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
                  
                  <div className="space-y-4">
                    {/* <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Client <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.clientId}
                        onChange={handleClientChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Choose a client</option>
                        {clients.map(client => (
                          <option key={client._id} value={client._id}>{client.companyName}</option>
                        ))}
                      </select>
                    </div> */}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                        <input
                          type="text"
                          name="clientName"
                          value={formData.clientName}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client Email</label>
                        <input
                          type="email"
                          name="clientEmail"
                          value={formData.clientEmail}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">PO Number</label>
                        <input
                          type="text"
                          name="poNumber"
                          value={formData.poNumber}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Client GSTIN</label>
                        <input
                          type="text"
                          name="clientGSTIN"
                          value={formData.clientGSTIN}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">GST Status</label>
                        <select
                          name="gstStatus"
                          value={formData.gstStatus}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="Registered">Registered</option>
                          <option value="Unregistered">Unregistered</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Payment Terms</label>
                        <input
                          type="text"
                          name="paymentTerms"
                          value={formData.paymentTerms}
                          onChange={handleInputChange}
                          placeholder="e.g., Net 30"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing Address */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MapPin size={20} className="text-blue-600" />
                    Billing Address
                  </h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Street Line 1"
                      value={formData.billingAddress.streetLine1}
                      onChange={(e) => handleAddressChange('billingAddress', 'streetLine1', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Street Line 2"
                      value={formData.billingAddress.streetLine2}
                      onChange={(e) => handleAddressChange('billingAddress', 'streetLine2', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="City"
                        value={formData.billingAddress.city}
                        onChange={(e) => handleAddressChange('billingAddress', 'city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={formData.billingAddress.state}
                        onChange={(e) => handleAddressChange('billingAddress', 'state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Zipcode"
                        value={formData.billingAddress.zipcode}
                        onChange={(e) => handleAddressChange('billingAddress', 'zipcode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Country"
                        value={formData.billingAddress.country}
                        onChange={(e) => handleAddressChange('billingAddress', 'country', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <MapPin size={20} className="text-blue-600" />
                      Shipping Address
                    </h3>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        name="sameAsBilling"
                        checked={formData.sameAsBilling}
                        onChange={handleInputChange}
                        className="rounded border-gray-300"
                      />
                      Same as billing
                    </label>
                  </div>
                  {!formData.sameAsBilling && (
                    <div className="space-y-4">
                      <input
                        type="text"
                        placeholder="Street Line 1"
                        value={formData.shippingAddress.streetLine1}
                        onChange={(e) => handleAddressChange('shippingAddress', 'streetLine1', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Street Line 2"
                        value={formData.shippingAddress.streetLine2}
                        onChange={(e) => handleAddressChange('shippingAddress', 'streetLine2', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="City"
                          value={formData.shippingAddress.city}
                          onChange={(e) => handleAddressChange('shippingAddress', 'city', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="State"
                          value={formData.shippingAddress.state}
                          onChange={(e) => handleAddressChange('shippingAddress', 'state', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="Zipcode"
                          value={formData.shippingAddress.zipcode}
                          onChange={(e) => handleAddressChange('shippingAddress', 'zipcode', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          placeholder="Country"
                          value={formData.shippingAddress.country}
                          onChange={(e) => handleAddressChange('shippingAddress', 'country', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Placement Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Candidate Information</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Candidate <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.candidateId}
                        onChange={handleCandidateChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="">Choose a candidate</option>
                        {candidates.map(candidate => (
                          <option key={candidate.id} value={candidate.id}>
                            {candidate.firstName} {candidate.lastName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Designation <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation}
                        onChange={handleInputChange}
                        placeholder="e.g., Senior Software Engineer"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Placement Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="placementType"
                          value={formData.placementType}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        >
                          <option value="Permanent">Permanent</option>
                          <option value="Contract">Contract</option>
                          <option value="Contract-to-Hire">Contract-to-Hire</option>
                          <option value="Temporary">Temporary</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          CTC (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="ctc"
                          value={formData.ctc}
                          onChange={handleInputChange}
                          placeholder="1200000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Joining Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        name="joiningDate"
                        value={formData.joiningDate}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Fee Structure */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Fee Structure</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fee Model <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="feeModel"
                        value={formData.feeModel}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      >
                        <option value="Percentage">Percentage of CTC</option>
                        <option value="Fixed">Fixed Amount</option>
                        <option value="Hybrid">Hybrid (Percentage + Fixed)</option>
                      </select>
                    </div>

                    {(formData.feeModel === 'Percentage' || formData.feeModel === 'Hybrid') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fee Percentage (%) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="feePercentage"
                          value={formData.feePercentage}
                          onChange={handleInputChange}
                          placeholder="8.33"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    )}

                    {(formData.feeModel === 'Fixed' || formData.feeModel === 'Hybrid') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Fixed Amount (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="fixedAmount"
                          value={formData.fixedAmount}
                          onChange={handleInputChange}
                          placeholder="50000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          required
                        />
                      </div>
                    )}

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Calculated Fee</span>
                        <span className="text-2xl font-bold text-blue-600">
                          ₹{parseFloat(formData.calculatedFee || 0).toLocaleString('en-IN')}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Guarantee Days</label>
                        <input
                          type="number"
                          name="guaranteeDays"
                          value={formData.guaranteeDays}
                          onChange={handleInputChange}
                          placeholder="90"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Guarantee End Date</label>
                        <input
                          type="date"
                          value={formData.guaranteeEndDate}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">If Candidate Leaves</label>
                      <select
                        name="ifCandidateLeaves"
                        value={formData.ifCandidateLeaves}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="Full Refund">Full Refund</option>
                        <option value="Partial Refund">Partial Refund</option>
                        <option value="No Refund">No Refund</option>
                        <option value="Free Replacement">Free Replacement</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Exit Penalty (if applicable)</label>
                      <input
                        type="text"
                        name="exitPenalty"
                        value={formData.exitPenalty}
                        onChange={handleInputChange}
                        placeholder="e.g., 50% of fee"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Company & Documents */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Company Details</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your GSTIN</label>
                        <input
                          type="text"
                          name="yourGSTIN"
                          value={formData.yourGSTIN}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Your PAN</label>
                        <input
                          type="text"
                          name="yourPan"
                          value={formData.yourPan}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                        <input
                          type="text"
                          name="accountNumber"
                          value={formData.accountNumber}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code</label>
                        <input
                          type="text"
                          name="ifscCode"
                          value={formData.ifscCode}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Address */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Address</h3>
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Street Line 1"
                      value={formData.fromAddress.streetLine1}
                      onChange={(e) => handleAddressChange('fromAddress', 'streetLine1', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Street Line 2"
                      value={formData.fromAddress.streetLine2}
                      onChange={(e) => handleAddressChange('fromAddress', 'streetLine2', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="City"
                        value={formData.fromAddress.city}
                        onChange={(e) => handleAddressChange('fromAddress', 'city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={formData.fromAddress.state}
                        onChange={(e) => handleAddressChange('fromAddress', 'state', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        placeholder="Zipcode"
                        value={formData.fromAddress.zipcode}
                        onChange={(e) => handleAddressChange('fromAddress', 'zipcode', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        placeholder="Country"
                        value={formData.fromAddress.country}
                        onChange={(e) => handleAddressChange('fromAddress', 'country', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Settings */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Invoice Settings</h3>
                  <div className="space-y-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="isTDSApplicable"
                        checked={formData.isTDSApplicable}
                        onChange={handleInputChange}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">TDS Applicable</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="automaticPaymentReminders"
                        checked={formData.automaticPaymentReminders}
                        onChange={handleInputChange}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Enable Automatic Payment Reminders</span>
                    </label>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Send Invoice Email To</label>
                      <input
                        type="email"
                        name="sendInvoiceMailTo"
                        value={formData.sendInvoiceMailTo}
                        onChange={handleInputChange}
                        placeholder="client@example.com"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Existing Documents */}
                {existingFiles && existingFiles.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Existing Documents</h3>
                    <div className="space-y-2">
                      {existingFiles.map((file, index) => (
                        file && file.link ? (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200"
                          >
                            <div className="flex items-center gap-3">
                              <FileText size={20} className="text-gray-400" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {file.documentType || 'Document'}
                                </p>
                                <p className="text-xs text-gray-500">{file.link ? file.link.split('/').pop() : 'N/A'}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleDownloadFile(file.link)}
                                className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                              >
                                <Download size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => removeExistingFile(file.link)}
                                className="p-1 text-red-600 hover:bg-red-100 rounded"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ) : null
                      ))}
                    </div>
                  </div>
                )}

                {/* New File Upload */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Add New Documents</h3>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label
                      htmlFor="file-upload"
                      className="flex flex-col items-center justify-center cursor-pointer"
                    >
                      <Upload size={48} className="text-gray-400 mb-2" />
                      <span className="text-sm text-gray-600">Click to upload or drag and drop</span>
                      <span className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, XLS, XLSX (max 10MB each)</span>
                    </label>
                  </div>

                  {files.length > 0 && (
                    <div className="mt-4 space-y-2">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-green-50 rounded-md border border-green-200"
                        >
                          <div className="flex items-center gap-3">
                            <FileText size={20} className="text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeNewFile(index)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50 flex-shrink-0">
          <div>
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
              >
                Previous
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancel
            </button>
            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Invoice'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EditInvoiceModal;